// §4.8 Movements Ledger (office) — the audit view of the immutable ledger.
//
// Filterable table (date range · type · product · lot · unit · location ·
// source doc · performer, incl. agent principals), row-expand detail with a
// permission-gated REVERSE (confirmation required — a reversal writes a new
// ledger row and can never be undone), and CSV export of the page.
//
// §7 COST REDACTION, presence-based (lib/cost.ts): the module-06 serializer
// sends `unit_cost_minor: null` + `redacted: true` when it withholds a value.
// If NOT ONE row on the page carries a visible cost, the cost columns are not
// rendered at all — the doctrine rule "never reserve an empty column" — and a
// single line states whether that is because the values were withheld from this
// role or because the stock is genuinely unvalued (edge case 14). Those are
// different facts and a storekeeper must be able to tell them apart. Procurement
// (cost on purchase_order movements only) sees a MIXED page: the column renders,
// and the rows it may not see say so per-cell.
//
// The CSV is a serialization of exactly these rows, so a withheld column can
// never leak through the export.

import { todayIso } from "../../../../lib/business-dates";
import { enumLabel } from "../../../../lib/enum-label";
import { shortId } from "../../../../lib/short-id";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { FilterBar, type FilterChip } from "../../../../components/filter-bar";
import { EmptyState, ErrorState, TableSkeleton } from "../../../../components/states";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { useDebouncedValue } from "../../../../lib/use-debounced-value";
import { useTerm } from "../../../onboarding/hooks/terminology";
import { ProductPicker } from "../../catalogue/components/product-picker";
import { CursorPager } from "../components/filters";
import { MoneyCell, MoneyColumnNote, RefLabel } from "../components/inventory-ui";
import { LocationPicker } from "../components/location-picker";
import {
  useLocationPathIndex,
  useLotIndex,
  useMovements,
  usePrincipalIndex,
  useProductIndex,
  useReverseMovement,
  useStockUnitIndex,
  useStockUnits,
  type MovementsQuery,
  type StockMovement,
} from "../hooks/inventory-queries";
import { useInventoryPermissions } from "../hooks/permissions";
import { costCellKind, costPresence, showCostColumn } from "../lib/cost";
import { csvConventionFor, downloadCsv, toCsv } from "../../../../lib/csv";
import { inventoryErrorMessage } from "../lib/errors";
import {
  formatMeasure,
  formatTimestamp,
  localDayEndIso,
  localDayStartIso,
  formatMeasureWithUom,
} from "../lib/measure";

const MOVEMENT_TYPES = [
  "receipt",
  "production_output",
  "issue",
  "transfer_out",
  "transfer_in",
  "adjustment",
  "count_adjustment",
  "cut_out",
  "cut_in",
  "scrap",
  "return_to_supplier",
  "shipment",
  "repack",
  "reversal",
] as const;

const SOURCE_DOC_TYPES = [
  "purchase_order",
  "work_order",
  "sales_shipment",
  "transfer",
  "cycle_count",
  "cut",
  "waste_ticket",
  "manual_adjustment",
  "opening_balance",
] as const;

const PAGE_SIZE = 50;

export function MovementsLedgerScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { term } = useTerm();
  const perms = useInventoryPermissions();

  // §4.9's drill links land here with a filter in the URL (validateSearch on
  // the route, screens/search-params.ts). It seeds the INITIAL filter state; the
  // controls stay the source of truth afterwards, so a user can widen the view
  // without the URL fighting them.
  const search = useSearch({ strict: false });

  const [movementType, setMovementType] = useState(search.movement_type ?? "");
  const [sourceDocType, setSourceDocType] = useState(search.source_doc_type ?? "");
  // §4.12: a posted request's page links to the ONE movement it wrote
  // (`source_doc_type=manual_adjustment&source_doc_id={request}`, §3.9). A
  // filter the screen has no control for would be a trap, so it is shown as a
  // chip with a clear button below.
  const [sourceDocId, setSourceDocId] = useState(
    typeof search.source_doc_id === "string" ? search.source_doc_id : "",
  );
  const [productId, setProductId] = useState(search.product_id ?? "");
  const [lotId, setLotId] = useState(search.lot_id ?? "");
  const [serialQuery, setSerialQuery] = useState("");
  const [stockUnitId, setStockUnitId] = useState(search.stock_unit_id ?? "");
  const [locationId, setLocationId] = useState(search.location_id ?? "");
  const [performedBy, setPerformedBy] = useState("");
  const [movedFrom, setMovedFrom] = useState("");
  const [movedTo, setMovedTo] = useState("");
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [expanded, setExpanded] = useState<string | null>(null);

  const cursor = cursors[cursors.length - 1];

  // A storekeeper filters by BIN and by SERIAL, not by uuid. Both are now
  // type-to-search against the server: the location filter used to be a
  // `<select>` built from ONE 200-row page of the whole tenant's tree (staging
  // already holds ~500 rows), so most of a factory's bins could not be filtered
  // on at all. A serial is resolved through §5 row 12's `serial_q` into the id
  // the ledger filter actually takes; a location through §5 row 1's `q`.
  const debouncedSerial = useDebouncedValue(serialQuery, 250);
  const serialMatches = useStockUnits(
    { serial_q: debouncedSerial.trim(), limit: 20 },
    debouncedSerial.trim().length > 0,
  );
  const serialResolved = serialMatches.data?.items ?? [];

  const query = useMemo(() => {
    const next: MovementsQuery = { limit: PAGE_SIZE };
    if (cursor !== undefined) next.cursor = cursor;
    if (movementType.length > 0) next.movement_type = movementType;
    if (sourceDocType.length > 0) next.source_doc_type = sourceDocType;
    if (sourceDocId.length > 0) next.source_doc_id = sourceDocId;
    if (productId.length > 0) next.product_id = productId;
    if (lotId.length > 0) next.lot_id = lotId;
    if (stockUnitId.length > 0) next.stock_unit_id = stockUnitId;
    if (locationId.length > 0) next.location_id = locationId;
    if (performedBy.length > 0) next.performed_by = performedBy;
    // The table renders `moved_at` in the viewer's LOCAL time, so the window
    // has to be built from local midnights. A UTC-midnight bound silently
    // dropped rows this same table labelled as inside the range — and the CSV
    // export serialised that wrong window into a finance reconciliation.
    if (movedFrom.length > 0) {
      const from = localDayStartIso(movedFrom);
      if (from !== undefined) next.moved_from = from;
    }
    if (movedTo.length > 0) {
      const to = localDayEndIso(movedTo);
      if (to !== undefined) next.moved_to = to;
    }
    return next;
  }, [
    cursor,
    movementType,
    sourceDocType,
    sourceDocId,
    productId,
    lotId,
    stockUnitId,
    locationId,
    performedBy,
    movedFrom,
    movedTo,
  ]);

  const movements = useMovements(query);
  const reverse = useReverseMovement();
  const rows = useMemo(() => movements.data?.items ?? [], [movements.data]);
  const products = useProductIndex(
    rows.flatMap((row) => (row.product_id !== null ? [row.product_id] : [])),
  );
  const lots = useLotIndex(rows.flatMap((row) => (row.lot_id !== null ? [row.lot_id] : [])));
  const units = useStockUnitIndex(
    rows.flatMap((row) => (row.stock_unit_id !== null ? [row.stock_unit_id] : [])),
  );
  const principals = usePrincipalIndex(perms.canReadMembers);
  // The from/to labels for THIS page's rows — at most 2×PAGE_SIZE ids, asked for
  // by id (§5 row 1 `id=`). The old map was built from one 200-row page of the
  // tree, so any movement touching a bin past row 200 rendered a blank where the
  // ledger's whole point is saying where the stock went.
  const chosenLocation = useLocationPathIndex(locationId.length > 0 ? [locationId] : []);
  const locationPath = useLocationPathIndex(
    useMemo(() => rows.flatMap((row) => [row.from_location_id, row.to_location_id]), [rows]),
  );

  useEffect(() => {
    if (movements.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [movements.error, navigate]);

  const presence = costPresence(rows, (row) => row.unit_cost_minor);
  const withCost = showCostColumn(presence);
  const canReverse = perms.has("inventory.movement.reverse");

  function resetPaging() {
    setCursors([undefined]);
  }

  // Rev-7 FilterBar: every applied filter as a removable chip, in words.
  const filterChips: FilterChip[] = [];
  const chip = (key: string, label: string, value: string, clear: () => void): void => {
    filterChips.push({
      key,
      label: `${label}: ${value}`,
      onRemove: () => {
        clear();
        resetPaging();
      },
    });
  };
  if (movementType.length > 0) {
    chip(
      "type",
      t("inventory.movements.type"),
      t(`inventory.vocab.movement.${movementType}`),
      () => {
        setMovementType("");
      },
    );
  }
  if (productId.length > 0) {
    chip(
      "product",
      t("inventory.movements.product"),
      products.byId.get(productId)?.sku ?? shortId(productId),
      () => {
        setProductId("");
      },
    );
  }
  if (sourceDocType.length > 0) {
    chip(
      "source",
      t("inventory.movements.sourceDoc"),
      t(`inventory.vocab.sourceDoc.${sourceDocType}`),
      () => {
        setSourceDocType("");
      },
    );
  }
  if (lotId.length > 0) {
    chip("lot", term("lot"), shortId(lotId), () => {
      setLotId("");
    });
  }
  if (stockUnitId.length > 0) {
    chip(
      "unit",
      term("stock_unit"),
      serialQuery.length > 0 ? serialQuery : shortId(stockUnitId),
      () => {
        setStockUnitId("");
        setSerialQuery("");
      },
    );
  }
  if (locationId.length > 0) {
    chip(
      "location",
      term("location"),
      chosenLocation.byId.get(locationId)?.path ?? shortId(locationId),
      () => {
        setLocationId("");
      },
    );
  }
  if (performedBy.length > 0) {
    chip("performer", t("inventory.movements.performer"), shortId(performedBy), () => {
      setPerformedBy("");
    });
  }
  if (movedFrom.length > 0) {
    chip("from", t("inventory.movements.from"), movedFrom, () => {
      setMovedFrom("");
    });
  }
  if (movedTo.length > 0) {
    chip("to", t("inventory.movements.to"), movedTo, () => {
      setMovedTo("");
    });
  }
  function clearFilters(): void {
    setMovementType("");
    setProductId("");
    setSourceDocType("");
    setLotId("");
    setStockUnitId("");
    setSerialQuery("");
    setLocationId("");
    setPerformedBy("");
    setMovedFrom("");
    setMovedTo("");
    resetPaging();
  }

  /** The filters this export was taken under, as a header comment row. */
  function activeFilterSummary(): string {
    const parts: string[] = [];
    if (movementType.length > 0) parts.push(`movement_type=${movementType}`);
    if (sourceDocType.length > 0) parts.push(`source_doc_type=${sourceDocType}`);
    if (sourceDocId.length > 0) parts.push(`source_doc_id=${sourceDocId}`);
    if (productId.length > 0) parts.push(`product_id=${productId}`);
    if (lotId.length > 0) parts.push(`lot_id=${lotId}`);
    if (stockUnitId.length > 0) parts.push(`stock_unit_id=${stockUnitId}`);
    if (locationId.length > 0) parts.push(`location_id=${locationId}`);
    if (performedBy.length > 0) parts.push(`performed_by=${performedBy}`);
    if (movedFrom.length > 0) parts.push(`moved_from=${movedFrom}`);
    if (movedTo.length > 0) parts.push(`moved_to=${movedTo}`);
    return parts.length === 0 ? "none" : parts.join(" ");
  }

  function exportPage() {
    const header = withCost
      ? ([
          "moved_at",
          "movement_type",
          "product",
          "lot_id",
          "stock_unit_id",
          "from_location_id",
          "to_location_id",
          "quantity",
          "uom",
          "qty_base",
          "unit_cost_minor",
          "currency",
          "source_doc_type",
          "source_doc_id",
          "performed_by",
          "note",
        ] as const)
      : ([
          "moved_at",
          "movement_type",
          "product",
          "lot_id",
          "stock_unit_id",
          "from_location_id",
          "to_location_id",
          "quantity",
          "uom",
          "qty_base",
          "source_doc_type",
          "source_doc_id",
          "performed_by",
          "note",
        ] as const);

    // The export is THIS PAGE, not the filtered set — a finance user who hands
    // an auditor 50 of 4,000 movements believing it is the whole quarter has
    // been misled by a label. The scope and the filters travel inside the file,
    // because the file is what gets forwarded.
    const convention = csvConventionFor(i18n.language);
    const punctuation =
      convention.delimiter === ";"
        ? t("inventory.movements.exportPunctuationSemicolonComma")
        : t("inventory.movements.exportPunctuationCommaDot");
    const scopeRow: (string | number | null | undefined)[] = [
      `# ${t("inventory.movements.exportScopePlain", { n: rows.length })} · filters: ${activeFilterSummary()} · ${punctuation}`,
      ...header.slice(1).map(() => ""),
    ];

    const body = rows.map((row) => {
      const base = [
        row.moved_at,
        row.movement_type,
        row.product_id === null ? "" : (products.byId.get(row.product_id)?.sku ?? row.product_id),
        row.lot_id,
        row.stock_unit_id,
        row.from_location_id,
        row.to_location_id,
        row.quantity,
        row.uom,
        row.qty_base,
      ];
      const tail = [row.source_doc_type, row.source_doc_id, row.performed_by, row.note];
      if (!withCost) return [...base, ...tail];
      const kind = costCellKind(row, (r) => r.unit_cost_minor);
      return [
        ...base,
        kind === "value" ? row.unit_cost_minor : kind === "withheld" ? "redacted" : "unvalued",
        row.currency,
        ...tail,
      ];
    });
    downloadCsv(
      `movements-page-${todayIso()}.csv`,
      toCsv(header, [scopeRow, ...body], {
        convention,
        decimalHeaders: ["quantity", "qty_base"],
      }),
    );
  }

  const columns: DataTableColumn<StockMovement>[] = [
    {
      key: "moved_at",
      header: t("inventory.movements.colWhen"),
      id: true,
      // Rev-7 column kinds: a date and a quantity never break mid-token.
      kind: "date",
      render: (row) => formatTimestamp(row.moved_at, i18n.language),
    },
    {
      key: "movement_type",
      header: t("inventory.movements.colType"),
      render: (row) => enumLabel(t, "inventory.vocab.movement", row.movement_type),
    },
    {
      key: "product_id",
      header: t("inventory.movements.colProduct"),
      render: (row) =>
        row.product_id === null ? (
          "—"
        ) : (
          <RefLabel
            value={products.byId.get(row.product_id)?.sku}
            id={row.product_id}
            pending={products.isPending}
            kind="product"
          />
        ),
    },
    {
      key: "quantity",
      header: t("inventory.movements.colQuantity"),
      kind: "qty",
      render: (row) => formatMeasureWithUom(row.quantity, row.uom, { locale: i18n.language }),
    },
    // §7 redaction-by-absence: the column does not exist for a caller the
    // server did not serve `unit_cost_minor` to.
    ...(withCost
      ? [
          {
            key: "unit_cost_minor",
            header: t("inventory.money.unitCost"),
            numeric: true,
            render: (row: StockMovement) => (
              <MoneyCell
                row={row}
                valueOf={(r) => r.unit_cost_minor}
                currencyOf={(r) => r.currency}
              />
            ),
          },
        ]
      : []),
    {
      key: "source_doc_type",
      header: t("inventory.movements.colSource"),
      render: (row) =>
        row.source_doc_type === null
          ? "—"
          : enumLabel(t, "inventory.vocab.sourceDoc", row.source_doc_type),
    },
    {
      key: "expand",
      header: t("inventory.movements.colExpand"),
      actions: true,
      render: (row) => (
        <button
          type="button"
          className="ds-lnk"
          aria-expanded={expanded === row.id}
          onClick={() => {
            setExpanded((prev) => (prev === row.id ? null : row.id));
          }}
        >
          {expanded === row.id
            ? t("inventory.movements.collapse")
            : t("inventory.movements.expand")}
        </button>
      ),
    },
  ];

  return (
    <section className="movements-ledger">
      <PageHeader title={t("inventory.movements.title")} />

      {sourceDocId.length > 0 ? (
        <p className="degraded-note" role="note" data-testid="movements-source-doc">
          {t("inventory.movements.sourceDocFilter", { id: shortId(sourceDocId) })}{" "}
          <button
            type="button"
            className="ds-lnk"
            onClick={() => {
              setSourceDocId("");
              resetPaging();
            }}
          >
            {t("inventory.movements.clearFilter")}
          </button>
        </p>
      ) : null}
      <FilterBar
        data-testid="movements-filters"
        filters={{
          inline: (
            <>
              <label className="filter-chip">
                <span className="filter-chip-label">{t("inventory.movements.type")}</span>
                <select
                  value={movementType}
                  onChange={(event) => {
                    setMovementType(event.target.value);
                    resetPaging();
                  }}
                >
                  <option value="">{t("inventory.movements.allTypes")}</option>
                  {MOVEMENT_TYPES.map((value) => (
                    <option key={value} value={value}>
                      {t(`inventory.vocab.movement.${value}`)}
                    </option>
                  ))}
                </select>
              </label>
              <div className="filter-chip filter-chip-wide">
                <span className="filter-chip-label">{t("inventory.movements.product")}</span>
                <ProductPicker
                  value={productId}
                  onChange={(next) => {
                    setProductId(next);
                    resetPaging();
                  }}
                  placeholder={t("inventory.movements.productPlaceholder")}
                  resolveLabel={(pid) => products.byId.get(pid)?.sku}
                />
              </div>
            </>
          ),
          more: (
            <>
              <label className="filter-chip">
                <span className="filter-chip-label">{t("inventory.movements.sourceDoc")}</span>
                <select
                  value={sourceDocType}
                  onChange={(event) => {
                    setSourceDocType(event.target.value);
                    resetPaging();
                  }}
                >
                  <option value="">{t("inventory.movements.allSourceDocs")}</option>
                  {SOURCE_DOC_TYPES.map((value) => (
                    <option key={value} value={value}>
                      {t(`inventory.vocab.sourceDoc.${value}`)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="filter-chip">
                <span className="filter-chip-label">{term("lot")}</span>
                <input
                  value={lotId}
                  onChange={(event) => {
                    setLotId(event.target.value.trim());
                    resetPaging();
                  }}
                  placeholder={t("inventory.movements.idPlaceholder")}
                />
              </label>
              <div className="filter-chip filter-chip-wide">
                <span className="filter-chip-label">{term("stock_unit")}</span>
                {/* Type a SERIAL; the matches resolve to the id the filter takes. */}
                <input
                  value={serialQuery}
                  onChange={(event) => {
                    setSerialQuery(event.target.value);
                    if (event.target.value.trim().length === 0) {
                      setStockUnitId("");
                      resetPaging();
                    }
                  }}
                  placeholder={t("inventory.movements.serialPlaceholder")}
                  aria-label={t("inventory.movements.serialPlaceholder")}
                />
                {serialResolved.length > 0 && stockUnitId.length === 0 ? (
                  <ul className="picker-results">
                    {serialResolved.map((unit) => (
                      <li key={unit.id}>
                        <button
                          type="button"
                          onClick={() => {
                            setStockUnitId(unit.id);
                            setSerialQuery(unit.serial_code);
                            resetPaging();
                          }}
                        >
                          {unit.serial_code}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
                {stockUnitId.length > 0 ? (
                  <Button
                    onClick={() => {
                      setStockUnitId("");
                      setSerialQuery("");
                      resetPaging();
                    }}
                  >
                    {t("inventory.movements.clearFilter")}
                  </Button>
                ) : null}
              </div>
              <div className="filter-chip filter-chip-wide">
                <LocationPicker
                  testId="movements-location"
                  label={term("location")}
                  value={locationId}
                  onChange={(next) => {
                    setLocationId(next);
                    resetPaging();
                  }}
                  clearLabel={t("inventory.movements.allLocations")}
                />
              </div>
              <label className="filter-chip">
                <span className="filter-chip-label">{t("inventory.movements.performer")}</span>
                <input
                  value={performedBy}
                  onChange={(event) => {
                    setPerformedBy(event.target.value.trim());
                    resetPaging();
                  }}
                  placeholder={t("inventory.movements.performerPlaceholder")}
                />
              </label>
              <label className="filter-chip">
                <span className="filter-chip-label">{t("inventory.movements.from")}</span>
                <input
                  type="date"
                  value={movedFrom}
                  onChange={(event) => {
                    setMovedFrom(event.target.value);
                    resetPaging();
                  }}
                />
              </label>
              <label className="filter-chip">
                <span className="filter-chip-label">{t("inventory.movements.to")}</span>
                <input
                  type="date"
                  value={movedTo}
                  onChange={(event) => {
                    setMovedTo(event.target.value);
                    resetPaging();
                  }}
                />
              </label>
            </>
          ),
          // "Filters (2)" once something applies; plain "Filters" at rest.
          label:
            filterChips.length > 0
              ? t("inventory.filters.button", { n: filterChips.length })
              : t("inventory.filters.title"),
          title: t("inventory.filters.title"),
          doneLabel: t("inventory.filters.done"),
        }}
        chips={filterChips}
        removeLabel={(chip) => t("inventory.filters.remove", { label: chip.label })}
        clearAll={{ label: t("inventory.filters.clearAll"), onClear: clearFilters }}
        actions={
          <Button onClick={exportPage} disabled={rows.length === 0}>
            {t("inventory.movements.exportPage", { n: rows.length })}
          </Button>
        }
      />

      {movements.isPending ? (
        <TableSkeleton rows={8} cols={6} label={t("inventory.movements.loading")} />
      ) : null}

      {movements.error && !(movements.error instanceof UnauthorizedError) ? (
        <ErrorState
          title={t("inventory.movements.errorTitle")}
          detail={movements.error.message}
          retryLabel={t("inventory.common.retry")}
          onRetry={() => void movements.refetch()}
          requestId={requestIdOf(movements.error)}
          requestIdLabel={t("inventory.common.requestId")}
        />
      ) : null}

      {movements.data !== undefined && rows.length === 0 ? (
        <EmptyState
          title={t("inventory.movements.emptyTitle")}
          body={t("inventory.movements.emptyBody")}
        />
      ) : null}

      {rows.length > 0 ? (
        <>
          <MoneyColumnNote presence={presence} />
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(row) => row.id}
            empty={null}
            // The §3.3 expansion contract: the detail block is attached to the
            // movement it explains. It used to be a hand-spliced `<tr
            // colSpan>` — the same shape, invisible to the phone collapse and
            // unreachable by the primitive.
            expansion={(row) =>
              expanded === row.id ? (
                <MovementDetail
                  row={row}
                  canReverse={canReverse}
                  fromPath={locationPath.pathOf(row.from_location_id)}
                  toPath={locationPath.pathOf(row.to_location_id)}
                  performer={principals.byId.get(row.performed_by)}
                  principalsPending={principals.isPending}
                  lotCode={row.lot_id === null ? undefined : lots.byId.get(row.lot_id)?.lot_code}
                  lotsPending={lots.isPending}
                  serialCode={
                    row.stock_unit_id === null
                      ? undefined
                      : units.byId.get(row.stock_unit_id)?.serial_code
                  }
                  unitsPending={units.isPending}
                  locale={i18n.language}
                  onReverse={(note) => {
                    reverse.mutate({ id: row.id, ...(note.length > 0 ? { note } : {}) });
                  }}
                  reverseState={
                    reverse.variables?.id === row.id
                      ? {
                          pending: reverse.isPending,
                          error: reverse.isError
                            ? inventoryErrorMessage(
                                t,
                                reverse.error,
                                "inventory.movements.reverseFailed",
                              )
                            : undefined,
                          success: reverse.isSuccess,
                        }
                      : undefined
                  }
                />
              ) : null
            }
            expansionLabel={() => t("inventory.movements.colExpand")}
          />
          <CursorPager
            canPrev={cursors.length > 1}
            nextCursor={movements.data?.next_cursor}
            onPrev={() => {
              setCursors((prev) => prev.slice(0, -1));
            }}
            onNext={(next) => {
              setCursors((prev) => [...prev, next]);
            }}
          />
        </>
      ) : null}
    </section>
  );
}

function MovementDetail({
  row,
  canReverse,
  fromPath,
  toPath,
  performer,
  principalsPending,
  lotCode,
  lotsPending,
  serialCode,
  unitsPending,
  onReverse,
  reverseState,
  locale,
}: {
  row: StockMovement;
  canReverse: boolean;
  fromPath: string | undefined;
  toPath: string | undefined;
  performer: string | undefined;
  principalsPending: boolean;
  lotCode: string | undefined;
  lotsPending: boolean;
  serialCode: string | undefined;
  unitsPending: boolean;
  onReverse: (note: string) => void;
  reverseState: { pending: boolean; error: string | undefined; success: boolean } | undefined;
  locale: string;
}) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const [note, setNote] = useState("");
  // A REVERSAL cannot itself be reversed (§3.9 REVERSAL_NOT_REVERSIBLE), and
  // that is the only half of the rule the wire can support: an already-reversed
  // ORIGINAL carries no marker — `reversal_of_movement_id` is set on the
  // reversal, and `StockMovement` has no `reversed_by_movement_id` — so
  // MOVEMENT_ALREADY_REVERSED is a server refusal surfaced through
  // lib/errors.ts, not something this row can predict. §4.8 amended to say so.
  const reversible = row.movement_type !== "reversal";

  return (
    <>
      <dl className="inv-fields">
        <div className="inv-field">
          <dt>{t("inventory.movements.colFrom")}</dt>
          <dd>
            <RefLabel value={fromPath} id={row.from_location_id} kind="location" />
          </dd>
        </div>
        <div className="inv-field">
          <dt>{t("inventory.movements.colTo")}</dt>
          <dd>
            <RefLabel value={toPath} id={row.to_location_id} kind="location" />
          </dd>
        </div>
        <div className="inv-field">
          <dt>{t("inventory.movements.colQtyBase")}</dt>
          <dd>{formatMeasure(row.qty_base, { locale })}</dd>
        </div>
        <div className="inv-field">
          <dt>{t("inventory.movements.colPerformer")}</dt>
          <dd>
            {/* §4.8 names "performer incl. agent principals": an agent is
                      not a member, so it resolves to no name and renders as an
                      id chip rather than as a broken person. */}
            <RefLabel
              value={performer}
              id={row.performed_by}
              pending={principalsPending}
              kind="user"
            />
          </dd>
        </div>
        <div className="inv-field">
          <dt>{t("inventory.movements.colReason")}</dt>
          <dd>
            {row.reason_code === null
              ? "—"
              : enumLabel(t, "inventory.vocab.reason", row.reason_code)}
          </dd>
        </div>
        <div className="inv-field">
          <dt>{t("inventory.movements.colNote")}</dt>
          <dd>{row.note ?? "—"}</dd>
        </div>
        {row.stock_unit_id !== null ? (
          <div className="inv-field">
            <dt>{t("inventory.movements.colUnit")}</dt>
            <dd>
              <Link to="/inventory/units/$id" params={{ id: row.stock_unit_id }}>
                <RefLabel
                  value={serialCode}
                  id={row.stock_unit_id}
                  pending={unitsPending}
                  kind="stock_unit"
                />
              </Link>
            </dd>
          </div>
        ) : null}
        {row.lot_id !== null ? (
          <div className="inv-field">
            <dt>{t("inventory.movements.colLot")}</dt>
            <dd>
              <Link to="/inventory/lots/$id" params={{ id: row.lot_id }}>
                <RefLabel value={lotCode} id={row.lot_id} pending={lotsPending} kind="lot" />
              </Link>
            </dd>
          </div>
        ) : null}
      </dl>

      {canReverse && reversible ? (
        confirming ? (
          <form
            className="inline-form"
            onSubmit={(event) => {
              event.preventDefault();
              onReverse(note.trim());
              setConfirming(false);
            }}
          >
            <p className="inv-warning">{t("inventory.movements.reverseWarning")}</p>
            <Field label={t("inventory.movements.reverseNote")}>
              <input
                className="ds-input"
                value={note}
                onChange={(event) => {
                  setNote(event.target.value);
                }}
              />
            </Field>
            <div className="form-actions">
              <Button type="submit" variant="primary">
                {t("inventory.movements.reverseConfirm")}
              </Button>
              <Button
                onClick={() => {
                  setConfirming(false);
                }}
              >
                {t("inventory.common.cancel")}
              </Button>
            </div>
          </form>
        ) : (
          <Button
            onClick={() => {
              setConfirming(true);
            }}
          >
            {t("inventory.movements.reverse")}
          </Button>
        )
      ) : null}

      {reverseState?.pending === true ? (
        <p className="inv-hint">{t("inventory.movements.reversing")}</p>
      ) : null}
      {reverseState?.success === true ? (
        <p className="inv-success" role="status">
          {t("inventory.movements.reversed")}
        </p>
      ) : null}
      {reverseState?.error !== undefined ? (
        <p className="form-error" role="alert">
          {reverseState.error}
        </p>
      ) : null}
    </>
  );
}
